import React, { createContext, useContext } from 'react';
import { useTBStates } from '../hooks/useTBStates';
import type { tbEvent, ViewEventData, EventGroup, MomentUser, PaceOfPlayRow, EventGroupType } from '@moment-golf/types';

export interface TBContextValue {
    appSection: string;
    setAppSection: (v: string) => void;
    viewPanel: string;
    setViewPanel: (v: string) => void;
    groupTypeFilter: EventGroupType | 'all';
    setGroupTypeFilter: React.Dispatch<React.SetStateAction<EventGroupType | 'all'>>;
    factor: number;
    setFactor: (v: number) => void;
    appTheme: ReturnType<typeof import("../../shared/Settings/appTheme").setAppTheme>;
    setTheme: (v: ReturnType<typeof import("../../shared/Settings/appTheme").setAppTheme>) => void;
    eventData: tbEvent[];
    setEventData: React.Dispatch<React.SetStateAction<tbEvent[]>>;
    viewEvent: tbEvent | null;
    setViewEvent: React.Dispatch<React.SetStateAction<tbEvent | null>>;
    viewEventData: ViewEventData;
    setViewEventData: React.Dispatch<React.SetStateAction<ViewEventData>>;
    editMode: boolean;
    setEditMode: (v: boolean) => void;
    eventTab: string;
    setEventTab: (v: string) => void;
    eventTabValue: number;
    setEventTabValue: (v: number) => void;
    roundTab: string;
    setRoundTab: (v: string) => void;
    roundTabValue: number;
    setRoundTabValue: (v: number) => void;
    roundValue: number;
    setRoundValue: React.Dispatch<React.SetStateAction<number>>;
    groupData: EventGroup[];
    setGroupData: React.Dispatch<React.SetStateAction<EventGroup[]>>;
    viewGroup: EventGroup | null;
    setViewGroup: React.Dispatch<React.SetStateAction<EventGroup | null>>;
    groupTabValue: number;
    setGroupTabValue: (v: number) => void;
    optionsLoaded: unknown[];
    setOptionsLoaded: React.Dispatch<React.SetStateAction<unknown[]>>;
    forceUpdate: boolean;
    setForceUpdate: (v: boolean) => void;
    userData: unknown;
    setUserData: React.Dispatch<React.SetStateAction<unknown>>;
    selectedUser: MomentUser | null;
    setSelectedUser: React.Dispatch<React.SetStateAction<MomentUser | null>>;
    originalViewEvent: React.MutableRefObject<tbEvent | null>;
    originalViewEventData: React.MutableRefObject<ViewEventData>;
    originalRegistrations: React.MutableRefObject<import("../../shared/config/iTournamentBuilder").TbRegistration[]>;
    originalScores: React.MutableRefObject<unknown[]>;
    paceData: PaceOfPlayRow[];
    setPaceData: React.Dispatch<React.SetStateAction<PaceOfPlayRow[]>>;
    paceDataLastFetched: string | null;
    setPaceDataLastFetched: React.Dispatch<React.SetStateAction<string | null>>;
}

const TBContext = createContext<TBContextValue | null>(null);

export const TBProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const tbStates = useTBStates();
    return (
        <TBContext.Provider value={tbStates as TBContextValue}>
            {children}
        </TBContext.Provider>
    );
};

export const useTBContext = (): TBContextValue => {
    const context = useContext(TBContext);
    if (!context) {
        throw new Error('useTBContext must be used within a TBProvider');
    }
    return context;
};